import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

function formatErr(detail) {
  if (!detail) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/admin/dashboard");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="noise-overlay flex min-h-screen items-center justify-center bg-ink px-5" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md border border-white/10 bg-ink-surface p-8 md:p-10"
      >
        <div className="mb-8">
          <div className="flex h-11 w-11 items-center justify-center border border-volt/40 text-volt">
            <Lock size={18} />
          </div>
          <h1 className="mt-5 font-heading text-2xl font-black uppercase tracking-tight text-white">
            Admin Access
          </h1>
          <p className="mt-1 text-sm text-white/50">Rent A Car Kohima — Vehicle Management</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/50">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-email-input"
              className="w-full border border-white/15 bg-ink px-4 py-3 text-sm text-white outline-none transition-colors focus:border-volt"
              placeholder="admin@rentacarkohima.com"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/50">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-password-input"
              className="w-full border border-white/15 bg-ink px-4 py-3 text-sm text-white outline-none transition-colors focus:border-volt"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p className="text-sm text-destructive" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="flex w-full items-center justify-center gap-2 bg-volt py-3.5 text-sm font-bold uppercase tracking-wider text-ink transition-colors hover:bg-volt-hover disabled:opacity-60"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : "Sign In"}
          </button>
        </form>

        <a href="/" className="mt-6 block text-center text-xs font-semibold uppercase tracking-wider text-white/40 hover:text-volt">
          ← Back to website
        </a>
      </motion.div>
    </div>
  );
}
